import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { CyDeleteEntriesModal } from "./CyDeleteEntriesModal";
import { useTranslation } from "react-i18next";

export interface DeletableEntry {
  id: string;
  title: string;
}

interface Props<E extends DeletableEntry> {
  isModalOpen: boolean;
  setIsModalOpen: (value: ((prevState: boolean) => boolean) | boolean) => void;
  onEntriesDeleted?: () => void;
  selectedEntries: readonly E[];
  deleteEntries: (entries: readonly E[]) => void;
  modalTitle?: string;
  modalMessage?: string;
}

export function DeleteEntriesModal<E extends DeletableEntry>({
  isModalOpen,
  setIsModalOpen,
  onEntriesDeleted,
  selectedEntries,
  deleteEntries,
  modalTitle,
  modalMessage,
}: Props<E>) {
  const { t } = useTranslation();
  const title = modalTitle ?? t("common.deleteEntries");
  const message = modalMessage ?? t("common.deleteEntriesConfirm");
  const toggle = () => setIsModalOpen((v) => !v);

  function deleteSelectedEntries() {
    deleteEntries(selectedEntries);
    if (onEntriesDeleted) {
      onEntriesDeleted();
    }
    setIsModalOpen(false);
  }

  return (
    <Modal
      isOpen={isModalOpen}
      fullscreen="md"
      toggle={toggle}
      data-cy={CyDeleteEntriesModal.DeleteEntriesModal}
    >
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>
        <p>{message}</p>
        <ul>
          {selectedEntries.map((e) => (
            <li key={e.id}>{e.title}</li>
          ))}
        </ul>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("common.buttons.cancel")}
        </Button>{" "}
        <Button
          color="primary"
          onClick={() => deleteSelectedEntries()}
          data-cy={CyDeleteEntriesModal.ButtonConfirmDeletion}
        >
          {t("common.buttons.delete")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
